/* Tela 4 · Nota autorizada (US05): chave de acesso com cópia, PDF e XML, e-mail ao tomador,
   duplicar, substituir e cancelar, com o DANFSe ao lado. Também mostra notas canceladas
   e substituídas, e manda rascunho/rejeitada de volta ao formulário. */
import React from 'react';
import { Box, Flex, Grid, Heading, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowsClockwise,
  CheckCircle,
  Code,
  CopySimple,
  DownloadSimple,
  FileText,
  Plus,
  Prohibit,
  XCircle,
} from 'phosphor-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { useAuth } from '../../../../../providers/auth';
import {
  cnpjMask as mascara,
  agruparChave,
  Banner,
  brl,
  C,
  Card,
  CardHead,
  dataBR,
  EnvBadge,
  Eyebrow,
  horaBR,
  IconTile,
  ModuleHeader,
  MONO,
  NButton,
  StatusTag,
  Tone,
} from '../shared/ui';
import {
  editarPath,
  NFSE_ACOES,
  NFSE_EMISSAO_PATH,
  NFSE_PATHS,
  notaPath,
} from '../shared/constants';
import { DanfsePreview, Historico, useAcoesNota } from '../shared/notaDetalhe';
import { island } from '../shared/NfseIsland';
import { Carregando, Cascata, NfsePage } from '../shared/NfsePage';
import { CancelarModal } from './components/CancelarModal';
import { EmailForm } from './components/EmailForm';

function Dado({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <>
      <Text as="dt" fontSize="13px" color={C.subtle}>
        {rotulo}
      </Text>
      <Text as="dd" fontSize="13px" color={C.ink} m={0}>
        {children ?? '—'}
      </Text>
    </>
  );
}

const HERO: Record<string, { icon: React.ComponentType<any>; tone: Tone; titulo: string }> = {
  autorizada: { icon: CheckCircle, tone: 'success', titulo: 'Autorizada pela Sefin Nacional' },
  cancelada: { icon: Prohibit, tone: 'danger', titulo: 'Cancelada na Sefin Nacional' },
  substituida: { icon: ArrowsClockwise, tone: 'blue', titulo: 'Substituída por outra nota' },
};

export function NfseEmissaoNotaPage() {
  const navigate = useNavigate();
  const { itemId } = useParams();
  const id = Number(itemId);
  const { hasPermission } = useAuth();
  const { getEmissao } = useNfseEmissao();
  const acoes = useAcoesNota();

  const { data: nota, isLoading } = useQuery({
    queryKey: ['nfse-emissao', id],
    queryFn: getEmissao,
  });
  const podeEmitir = hasPermission(NFSE_EMISSAO_PATH, NFSE_ACOES.emitir);
  const podeCancelar = hasPermission(NFSE_EMISSAO_PATH, NFSE_ACOES.cancelar);

  if (isLoading || !nota) {
    return (
      <NfsePage wide>
        {isLoading ? <Carregando /> : <Banner tone="warning">Nota não encontrada.</Banner>}
      </NfsePage>
    );
  }

  const hero = HERO[nota.status];
  // Dedução da base do ISS (TI-2026-0358): em % vira R$ sobre o valor do serviço.
  const ded = Number(nota.form?.deducao) || 0;
  const deducao =
    nota.form?.deducao_tipo === 'percentual' ? Math.round((nota.v_serv ?? 0) * ded) / 100 : ded;
  const titulo = nota.n_nfse
    ? `NFS-e nº ${nota.n_nfse}`
    : `DPS ${nota.serie} · nº ${nota.n_dps ?? '—'}`;
  const copiar = async () => {
    await navigator.clipboard.writeText(nota.chave_acesso ?? '');
    island.notify('Chave de acesso copiada', { tone: 'ok', meta: '50 dígitos' });
  };

  return (
    <NfsePage wide ambiente={nota.ambiente}>
      <ModuleHeader
        title={titulo}
        icon={FileText}
        tone={nota.status === 'autorizada' ? 'green' : 'blue'}
        crumbs={[
          { label: 'Emissão de NFS-e', to: NFSE_PATHS.painel },
          { label: 'Notas emitidas', to: NFSE_PATHS.notas },
          { label: nota.n_nfse ? `Nº ${nota.n_nfse}` : `DPS ${nota.n_dps ?? ''}` },
        ]}
        actions={
          <>
            <EnvBadge ambiente={nota.ambiente} />
            {podeEmitir && (
              <NButton variant="secondary" icon={Plus} onClick={() => navigate(NFSE_PATHS.emitir)}>
                Emitir outra
              </NButton>
            )}
          </>
        }
      />

      {!hero && (
        <Banner
          tone={nota.status === 'rejeitada' ? 'danger' : 'info'}
          icon={nota.status === 'rejeitada' ? XCircle : undefined}
          action={
            <NButton size="sm" variant="secondary" onClick={() => navigate(editarPath(nota.id))}>
              {nota.status === 'rejeitada' ? 'Corrigir e reenviar' : 'Continuar'}
            </NButton>
          }
        >
          {nota.status === 'rejeitada'
            ? 'A Sefin rejeitou esta DPS. O número continua reservado para ela.'
            : 'Esta nota ainda é um rascunho: a DPS só ganha número no envio.'}
        </Banner>
      )}

      <Grid
        templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 380px' }}
        gap="18px"
        alignItems="start"
      >
        <Flex direction="column" gap="18px" minW={0}>
          {hero && (
            <Cascata i={0}>
              <Card p={6}>
                <Flex align="center" gap={5} wrap="wrap">
                  <IconTile icon={hero.icon} tone={hero.tone} size={60} iconSize={30} />
                  <Box flex="1 1 260px">
                    <StatusTag kind={nota.status} />
                    <Heading as="h2" fontSize="20px" fontWeight={700} color={C.ink} mt={2} mb={0.5}>
                      {hero.titulo}
                    </Heading>
                    <Text fontSize="13px" color={C.muted}>
                      DPS série {nota.serie}, nº {nota.n_dps} processada em{' '}
                      {dataBR(nota.dh_processamento)} às {horaBR(nota.dh_processamento)}. O XML e o
                      PDF já estão salvos na nossa base.
                    </Text>
                  </Box>
                  <Box textAlign="right">
                    <Eyebrow>Valor do serviço</Eyebrow>
                    <Text
                      fontFamily={MONO}
                      fontSize="26px"
                      lineHeight="30px"
                      fontWeight={600}
                      letterSpacing="-0.5px"
                      mt={1}
                    >
                      {brl(nota.v_serv)}
                    </Text>
                    {deducao > 0 && (
                      <Text fontSize="12px" color={C.muted} mt={1}>
                        Dedução {brl(deducao)} · base {brl((nota.v_serv ?? 0) - deducao)}
                      </Text>
                    )}
                  </Box>
                </Flex>
                {nota.status === 'substituida' && nota.substituida_por_id && (
                  <Box mt={4}>
                    <Banner
                      tone="info"
                      compact
                      action={
                        <NButton
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(notaPath(nota.substituida_por_id as number))}
                        >
                          Ver a nova nota
                        </NButton>
                      }
                    >
                      Substituída pela NFS-e nº {nota.substituida_por_n_nfse ?? '—'}.
                    </Banner>
                  </Box>
                )}
                {nota.status === 'cancelada' && nota.cancelamento_justificativa && (
                  <Box mt={4}>
                    <Banner tone="neutral" compact>
                      Cancelada em {dataBR(nota.cancelado_em)} · {nota.cancelamento_justificativa}
                    </Banner>
                  </Box>
                )}
              </Card>
            </Cascata>
          )}

          <Cascata i={1}>
            <Card p={6}>
              <CardHead
                title="Chave de acesso"
                right={
                  <Text fontSize="12px" color={C.subtle}>
                    50 dígitos
                  </Text>
                }
              />
              <Flex
                align="center"
                gap={3}
                p="12px 14px"
                borderRadius="14px"
                bg={C.sunken}
                boxShadow={`inset 0 0 0 1px ${C.line}`}
                wrap="wrap"
              >
                <Text
                  as="code"
                  flex={1}
                  fontFamily={MONO}
                  fontSize="12.5px"
                  color={C.ink}
                  wordBreak="break-word"
                >
                  {nota.chave_acesso
                    ? agruparChave(nota.chave_acesso)
                    : 'Sai junto com a autorização'}
                </Text>
                {nota.chave_acesso && (
                  <NButton size="sm" variant="secondary" icon={CopySimple} onClick={copiar}>
                    Copiar
                  </NButton>
                )}
              </Flex>

              {hero && (
                <Flex gap={2} wrap="wrap" mt={4} align="center">
                  <NButton
                    icon={DownloadSimple}
                    isLoading={acoes.baixando === `${nota.id}-pdf`}
                    onClick={() => acoes.baixar(nota, 'pdf')}
                  >
                    Baixar PDF
                  </NButton>
                  <NButton
                    variant="secondary"
                    icon={Code}
                    isLoading={acoes.baixando === `${nota.id}-xml`}
                    onClick={() => acoes.baixar(nota, 'xml')}
                  >
                    Baixar XML
                  </NButton>
                  <Box flex={1} />
                  {podeEmitir && (
                    <NButton
                      size="sm"
                      variant="ghost"
                      icon={CopySimple}
                      onClick={() => acoes.duplicar(nota)}
                    >
                      Duplicar
                    </NButton>
                  )}
                  {nota.status === 'autorizada' && podeCancelar && (
                    <>
                      <NButton
                        size="sm"
                        variant="ghost"
                        icon={ArrowsClockwise}
                        isLoading={acoes.substituir.isPending}
                        onClick={() => acoes.substituir.mutate(nota)}
                      >
                        Substituir
                      </NButton>
                      <NButton
                        size="sm"
                        variant="ghost"
                        icon={Prohibit}
                        onClick={() => acoes.abrirCancelamento(nota)}
                      >
                        Cancelar
                      </NButton>
                    </>
                  )}
                </Flex>
              )}

              <Box h="1px" bg={C.line} my={5} />
              <Grid as="dl" templateColumns="170px 1fr" rowGap={2} columnGap={4}>
                <Dado rotulo="Emitente">
                  {nota.emitente_razao_social} · {mascara(nota.emitente_cnpj)}
                </Dado>
                <Dado rotulo="Tomador">
                  {nota.tomador_nome}
                  {nota.tomador_doc ? ` · ${mascara(nota.tomador_doc)}` : ''}
                </Dado>
                <Dado rotulo="Serviço">
                  {nota.c_trib_nac} · competência{' '}
                  {nota.competencia
                    ? `${nota.competencia.slice(5, 7)}/${nota.competencia.slice(0, 4)}`
                    : '—'}
                </Dado>
                <Dado rotulo="Alertas da Sefin">
                  {nota.alertas?.length
                    ? nota.alertas.map(a => `${a.codigo} · ${a.descricao}`).join('; ')
                    : 'Nenhum'}
                </Dado>
              </Grid>
            </Card>
          </Cascata>

          {nota.status === 'autorizada' && (
            <Cascata i={2}>
              <Card as="section" p={6} aria-labelledby="h-mail">
                <CardHead
                  id="h-mail"
                  title="Enviar por e-mail"
                  description="Vão anexados o PDF (DANFSe) e o XML da nota"
                />
                <EmailForm emissao={nota} />
              </Card>
            </Cascata>
          )}
        </Flex>

        <Card p={5}>
          <CardHead title="DANFSe" description="PDF gerado conforme a NT 008" />
          <DanfsePreview emissao={nota} altura={480} />
          <Box mt={5}>
            <Historico emissao={nota} />
          </Box>
        </Card>
      </Grid>

      <CancelarModal
        isOpen={!!acoes.cancelando}
        onClose={acoes.fecharCancelamento}
        numero={acoes.cancelando?.n_nfse}
        isLoading={acoes.cancelar.isPending}
        onConfirmar={payload =>
          acoes.cancelando && acoes.cancelar.mutate({ nota: acoes.cancelando, payload })
        }
      />
    </NfsePage>
  );
}
